/**
 * components/Onboarding/modals/OnboardingReminderModal.tsx
 * Reminder modal for users who haven't completed onboarding
 *
 * Provides a gentle nudge to complete setup with options to:
 * - Complete setup now
 * - Remind me later
 * - Don't show again (marks onboarding as complete)
 */

// 1. External dependencies
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, ArrowRight } from 'lucide-react';

// 2. Internal absolute imports
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Checkbox } from '@enfomentis/ui';
import { userService } from '@/services/UserService';
import { Brand } from '@enfomentis/shared/config';

/**
 * Props for OnboardingReminderModal
 */
interface OnboardingReminderModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** User ID for updating onboarding status */
  userId: string;
  /** Callback when modal is closed */
  onClose: () => void;
}

/**
 * Onboarding reminder modal component
 *
 * Shows users who skipped onboarding a friendly reminder with options
 */
export const OnboardingReminderModal: React.FC<OnboardingReminderModalProps> = ({
  isOpen,
  userId,
  onClose
}) => {
  const navigate = useNavigate();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Handle "Complete Setup" button click (or "Skip Setup" if don't show again is checked)
   */
  const handlePrimaryAction = async (): Promise<void> => {
    if (dontShowAgain) {
      // User wants to skip setup permanently
      setIsProcessing(true);

      try {
        // Mark onboarding as complete in Supabase
        await userService.completeOnboarding(userId);

        // Store in localStorage
        localStorage.setItem(`${Brand.cachePrefix}-onboarding-reminder-dismissed`, 'true');

        console.log('✅ OnboardingReminderModal: User skipped onboarding permanently');
        onClose();
      } catch (error) {
        console.error('❌ OnboardingReminderModal: Error updating onboarding status:', error);
        // Still close the modal even if update fails
        localStorage.setItem(`${Brand.cachePrefix}-onboarding-reminder-dismissed`, 'true');
        onClose();
      } finally {
        setIsProcessing(false);
      }
    } else {
      // User wants to complete setup
      navigate('/onboarding');
    }
  };

  /**
   * Handle "Remind Me Later" button click
   */
  const handleRemindLater = (): void => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleRemindLater();
        }
      }}
      size="lg"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning-100">
              <Lightbulb className="w-6 h-6 text-warning-600" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary">
              Complete Your Setup
            </h2>
          </div>
        </ModalHeader>
        <ModalBody className="gap-4">
          <p className="text-text-secondary">
            We noticed you haven't completed your journal setup yet. Completing setup helps you:
          </p>
          <ul className="space-y-2 text-text-secondary">
            <li className="flex items-start gap-2">
              <ArrowRight className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span>Configure your trading accounts and strategies</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span>Set up playbooks and risk management rules</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span>Get the most accurate analytics and insights</span>
            </li>
          </ul>
          <p className="text-sm text-text-secondary">
            It only takes a few minutes and you can always skip sections you're not ready for.
          </p>
          <Checkbox
            isSelected={dontShowAgain}
            onValueChange={setDontShowAgain}
          >
            <span className="text-sm text-text-secondary">
              Don't show this again
            </span>
          </Checkbox>
        </ModalBody>
        <ModalFooter className="gap-2">
          <Button
            variant="light"
            onPress={handleRemindLater}
            isDisabled={isProcessing}
          >
            Remind Me Later
          </Button>
          <Button
            color={dontShowAgain ? 'default' : 'primary'}
            onPress={handlePrimaryAction}
            isDisabled={isProcessing}
            isLoading={isProcessing}
            endContent={!isProcessing && <ArrowRight className="w-4 h-4" />}
          >
            {dontShowAgain ? 'Skip Setup' : 'Complete Setup'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
